// @flow

import React from "react";
import { compose } from "redux";
import { localize } from "redux-i18n";
import { connect } from "react-redux";
import moment from "moment";
import { getReduxForm } from "@selectors";
import { route } from "@routes/helper";
import type { State } from "@store/reducers";
import { SectionComponent, Button, GSGrid, IconButton, View } from "@components";
import sharedActionCreator from "@modules/shared/actionCreator";
import type {
  EmployeeNonCancelledAbsencesContent,
  EmployeeAbsenceManagerNotes,
  ManagerNotesModal,
} from "@modules/shared/reducer/employee-absences/types";
import {
  getManagerNotes,
  getAddEditManagerNotesModal,
  getManagerNotesSchema,
  deleteNoteModalVisible,
  deleteNoteModalSelectedNote,
  employeeNonCancelledAbsences,
} from "@modules/shared/selectors/employee-absences";
import { AbsenceNotesFilter, AddManagerNotesModal } from "@modules/shared/components";
import "./styles.scss";
import { ConfirmationModal } from "@components";
import config from "@config";
import { selectUserType } from "@modules/account/selectors";

type PropsFromState = $Exact<{
  managerNotes: Array<EmployeeAbsenceManagerNotes>,
  addManagerNotesModal: ManagerNotesModal,
  isDeleteNoteModalVisible: boolean,
  selectedNote: ?EmployeeAbsenceManagerNotes,
  employeeNonCancelledAbsences: EmployeeNonCancelledAbsencesContent,
  managerNotesSchema: ?Object,
  userType: string,
  formData: Object,
}>;

type PropsFromDispatch = $Exact<{
  getManagerNotes: typeof sharedActionCreator.GetManagerNotes,
  exportManagerNotes: typeof sharedActionCreator.ExportManagerNotes,
  toggleAddEditManagerNotesModal: typeof sharedActionCreator.ToggleAddEditManagerNotesModal,
  getAbsenceManagerNotesSchema: typeof sharedActionCreator.GetAbsenceManagerNotesSchema,
  addAbsenceManagerNote: typeof sharedActionCreator.AddManagerNote,
  editAbsenceManagerNote: typeof sharedActionCreator.EditManagerNote,
  deleteManagerNote: typeof sharedActionCreator.DeleteManagerNote,
  toggleDeleteManagerNoteModal: typeof sharedActionCreator.ToggleDeleteManagerNoteModal,
  getEmployeeNonCancelledAbsences: typeof sharedActionCreator.GetEmployeeNonCancelledAbsences,
}>;

type Props = {
  clientId: number,
  employeeId: number,
  reporting?: boolean,
  ...PropsFromState,
  ...PropsFromLocalize,
  ...PropsFromDispatch,
};

type OwnState = {
  absenceId: ?number,
  managerNoteId: ?number,
};

class EmployeeAbsenceNotesSection extends React.Component<Props, OwnState> {
  state = {
    absenceId: null,
    managerNoteId: null,
  };

  get columns() {
    let cols = [
      {
        name: this.props.t("DATE_ADDED"),
        width: 150,
        renderCell: ({ row }: { row: EmployeeAbsenceManagerNotes }) =>
          moment(row.createdAt).format(config.dateTimeFormat),
        key: "dateAdded",
      },
      {
        name: this.props.t("ADDED_BY"),
        renderCell: ({ row }: { row: EmployeeAbsenceManagerNotes }) =>
          `${(row.createdBy || {}).firstName} ${(row.createdBy || {}).lastName}`,
        key: "addedBy",
        align: "left",
        width: "min-content",
      },
      {
        name: this.props.t("TYPE"),
        renderCell: ({ row }: { row: EmployeeAbsenceManagerNotes }) => (
          <div className="ellipsis">{row.noteType.name}</div>
        ),
        key: "type",
        width: 200,
        align: "left",
      },
      {
        name: this.props.t("NOTES"),
        cellClass: "p-0",
        renderCell: ({ row }: { row: EmployeeAbsenceManagerNotes }) => (
          <textarea
            rows="4"
            maxLength="600"
            className="no-resize background-color-white borders-solid-color-white fs-14 line-height-20"
            defaultValue={row.notes}
          />
        ),
        key: "notes",
        align: "left",
      },
      {
        name: this.props.t("RELATED_ABSENCE"),
        renderCell: ({ row }: { row: EmployeeAbsenceManagerNotes }) => {
          const absenceId = (row.absenceId || {}).id;

          return absenceId ? (
            <a
              href={route("REPORTING_EMPLOYEE_ABSENCE_DETAILS_PATH", {
                clientId: this.props.clientId,
                employeeId: this.props.employeeId,
                absenceId,
              })}
              target="_blank"
              rel="noopener noreferrer"
              className="pointer"
            >
              {absenceId}
            </a>
          ) : (
            ""
          );
        },
        key: "relatedAbsence",
        width: "min-content",
      },
    ];

    if (this.isAllowedToSee()) {
      cols.push({
        name: this.props.t("EDIT"),
        key: "editManagerNote",
        width: 80,
        frozen: "right",
        renderCell: ({ row }: { row: EmployeeAbsenceManagerNotes }) => {
          return (
            <IconButton
              icon={<span className="material-icons pointer font-size-26">edit</span>}
              onClick={this.showEditNotesModal(row)}
            />
          );
        },
      });
      cols.push({
        name: this.props.t("DELETE"),
        key: "toggleDeleteNoteModal",
        width: 80,
        frozen: "right",
        renderCell: ({ row }: { row: EmployeeAbsenceManagerNotes }) => {
          return (
            <span
              className="material-icons pointer font-size-26"
              onClick={this.toggleDeleteNoteModal(row)}
            >
              delete
            </span>
          );
        },
      });
    }

    return cols;
  }

  componentDidMount() {
    this.props.getAbsenceManagerNotesSchema();
    this.props.getEmployeeNonCancelledAbsences({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
    });
    this.onSearch();
  }

  toggleDeleteNoteModal = (item: EmployeeAbsenceManagerNotes | null) => () => {
    this.props.toggleDeleteManagerNoteModal({ note: item });
  };

  deleteManagerNote = () => {
    if (this.props.selectedNote) {
      this.props.deleteManagerNote({
        clientId: this.props.clientId,
        employeeId: this.props.employeeId,
        noteId: this.props.selectedNote.id,
      });
    }
  };

  showEditNotesModal = (item: Object) => () => {
    this.setState({ ...this.state, absenceId: (item.absenceId || {}).id });
    this.props.toggleAddEditManagerNotesModal({
      editMode: true,
      visible: true,
      data: item,
    });
  };

  showAddNotesModal = () => {
    this.setState({ ...this.state });
    this.props.toggleAddEditManagerNotesModal({
      editMode: false,
      visible: true,
      data: null,
    });
  };

  closeAddEditNotesModal = () => {
    this.props.toggleAddEditManagerNotesModal({
      editMode: false,
      visible: false,
      data: null,
    });
    this.setState({ ...this.state, absenceId: null });
  };

  isAllowedToSee = () => {
    const allowedUserTypes = ["ADMIN", "CLIENT_ADMIN", "SERVICE_MANAGER"];
    return allowedUserTypes.indexOf(this.props.userType) !== -1;
  };

  onExportAbsenceNotes = () => {
    this.props.exportManagerNotes({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      fromDate: this.formData.fromDate,
      toDate: this.formData.toDate
        ? moment(this.formData.toDate)
            .add(1, "days")
            .format(config.dateFormatBE) // adding one day so BE can search through the whole endDate (instead specifying hour 23:59:59)
        : null,
    });
  };

  submitAddManagerNotesModal = (formData: Object) => {
    let dataObject = {
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      note: {
        absenceId: (formData.absenceId || {}).id || null,
        noteType:
          formData.noteType && formData.noteType.id
            ? {
                id: formData.noteType.id,
              }
            : null,
        notes: formData.notes,
      },
    };

    if (dataObject.note.absenceId === -1) {
      delete dataObject.note.absenceId;
    }
    let managerNoteId;
    if (this.notesModalData && this.notesModalData.id !== null) {
      managerNoteId = this.notesModalData.id;
    }

    const addOrEdit = this.props.addManagerNotesModal.editMode
      ? this.props.editAbsenceManagerNote
      : this.props.addAbsenceManagerNote;

    addOrEdit({ ...dataObject, managerNoteId });
  };

  onSearch = () => {
    this.props.getManagerNotes({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      fromDate: this.formData.fromDate,
      toDate: this.formData.toDate
        ? moment(this.formData.toDate)
            .add(1, "days")
            .format(config.dateFormatBE) // adding one day so BE can search through the whole endDate (instead specifying hour 23:59:59)
        : null,
    });
  };

  get formData() {
    return (
      (this.props.formData || {}).values || {
        fromDate: moment()
          .subtract(365, "days")
          .format(config.dateFormatBE),
        toDate: moment().format(config.dateFormatBE),
      }
    );
  }

  get data() {
    const notesList = [...(this.props.managerNotes || [])];

    // sort newest at the top
    return notesList.sort((a: Object, b: Object) => b.id - a.id);
  }

  get schema() {
    const schema = JSON.parse(JSON.stringify(this.props.managerNotesSchema)); // with cloneObject function will complain about types
    const employeeNonCancelledAbsences =
      (this.props.employeeNonCancelledAbsences || {}).content || [];

    // when add note, display Relevant absence selector
    if (!this.props.addManagerNotesModal.editMode) {
      schema.properties = {
        ...schema.properties,
        absenceId: {
          type: "object",
          required: ["id"],
          properties: {
            id: {
              type: "integer",
              title: "Relevant Absence",
              oneOf: [
                {
                  title: this.props.t("NON_ABSENCE_SPECIFIC"),
                  const: -1,
                },
                ...(employeeNonCancelledAbsences.length
                  ? employeeNonCancelledAbsences.map(el => {
                      return {
                        const: el.id,
                        title: `Absence ID: ${el.id} (Start Date: ${moment(el.startsAt).format(
                          config.dateTimeFormat,
                        )})`,
                      };
                    })
                  : []),
              ],
            },
          },
        },
      };
    }

    return schema;
  }

  get notesModalData() {
    return this.props.addManagerNotesModal.data;
  }

  render() {
    return (
      <div className="employee-absence-notes-section">
        <View className="gs-container">
          <AbsenceNotesFilter
            onSubmit={this.onSearch}
            initVals={this.formData}
            onExportAbsenceNotes={this.onExportAbsenceNotes}
            showAddNotesModal={this.showAddNotesModal}
          />
          {this.props.addManagerNotesModal.visible && (
            <AddManagerNotesModal
              editMode={this.props.addManagerNotesModal.editMode}
              onDismissHandler={this.closeAddEditNotesModal}
              schema={this.schema}
              onSubmit={this.submitAddManagerNotesModal}
              data={this.notesModalData}
            />
          )}
          <GSGrid rows={this.data} columns={this.columns} rowHeight={r => 100} />
        </View>
        {this.props.isDeleteNoteModalVisible && (
          <ConfirmationModal
            headerText={this.props.t("DELETE_NOTE")}
            bodyContent={this.props.t("CONFIRM_NOTE_DELETION")}
            confirmBtnLabel={this.props.t("DELETE")}
            confirmBtnColor="negative"
            onCancel={this.toggleDeleteNoteModal(null)}
            onConfirm={this.deleteManagerNote}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  addManagerNotesModal: getAddEditManagerNotesModal(state),
  managerNotes: getManagerNotes(state),
  isDeleteNoteModalVisible: deleteNoteModalVisible(state),
  formData: getReduxForm(state)["absence-notes-filters"],
  managerNotesSchema: getManagerNotesSchema(state),
  selectedNote: deleteNoteModalSelectedNote(state),
  employeeNonCancelledAbsences: employeeNonCancelledAbsences(state),
  userType: selectUserType(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getManagerNotes: sharedActionCreator.GetManagerNotes,
  exportManagerNotes: sharedActionCreator.ExportManagerNotes,
  getAbsenceManagerNotesSchema: sharedActionCreator.GetAbsenceManagerNotesSchema,
  toggleAddEditManagerNotesModal: sharedActionCreator.ToggleAddEditManagerNotesModal,
  addAbsenceManagerNote: sharedActionCreator.AddManagerNote,
  editAbsenceManagerNote: sharedActionCreator.EditManagerNote,
  toggleDeleteManagerNoteModal: sharedActionCreator.ToggleDeleteManagerNoteModal,
  deleteManagerNote: sharedActionCreator.DeleteManagerNote,
  getEmployeeNonCancelledAbsences: sharedActionCreator.GetEmployeeNonCancelledAbsences,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(EmployeeAbsenceNotesSection);
